import React from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { Container, Title, Button, Group, Breadcrumbs, Anchor, Loader, Center } from '@mantine/core'
import { useQuery } from '@tanstack/react-query'
import { IconArrowLeft } from '@tabler/icons-react'
import { useAppTranslation } from 'hooks/useTranslation'

import { getCampaignById } from 'supabase/campaigns/queries'
import { EditCampaignForm } from 'components/campaigns/EditCampaignForm'
import { useCampaignUpdate } from 'hooks/campaigns/useCampaignUpdate'
import { CampaignUpdate } from 'supabase/campaigns/types'

export function EditCampaignPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { t } = useAppTranslation()
  const updateCampaign = useCampaignUpdate()

  // Own sub-key: getCampaignById is narrower than the detail page's getCampaignDetail,
  // so sharing ['campaign', id] let this row (no campaign_creatives) land in the
  // detail cache after a save. invalidateQueries(['campaign']) still matches both.
  const { data: campaign, isLoading, isFetchedAfterMount, error } = useQuery({
    queryKey: ['campaign', id, 'edit'],
    queryFn: () => getCampaignById(id!),
    enabled: !!id,
    // The form only sends fields that differ from this row, so it must not be seeded
    // from a cached copy another writer (SQL, another admin) has since changed.
    refetchOnMount: 'always'
  })

  const handleSubmit = async (data: CampaignUpdate) => {
    try {
      await updateCampaign.mutateAsync({ id: id!, data })
      navigate(`/campaigns/${id}`)
    } catch (error) {
      console.error('Error updating campaign:', error)
    }
  }

  // Wait for this mount's own fetch; a cached row would seed the form with stale values
  if (isLoading || (!!id && !isFetchedAfterMount)) {
    return (
      <Center h={400}>
        <Loader size="lg" />
      </Center>
    )
  }

  if (error || !campaign) {
    return (
      <Container>
        <Title order={3} c="red">
          {t('campaigns.error_loading') || 'Error loading campaign'}
        </Title>
        <Button mt="md" leftSection={<IconArrowLeft size={16} />} onClick={() => navigate('/campaigns')}>
          {t('campaigns.back_to_list') || 'Back to Campaigns'}
        </Button>
      </Container>
    )
  }

  const breadcrumbs = [
    { title: t('common.home') || 'Home', href: '/' },
    { title: t('campaigns.title') || 'Campaigns', href: '/campaigns' },
    { title: campaign.name, href: `/campaigns/${id}` },
    { title: t('common.edit') || 'Edit', href: '#' }
  ]

  return (
    <Container size="xl">
      <Breadcrumbs mb="md">
        {breadcrumbs.map((item, index) => (
          index < breadcrumbs.length - 1 ? (
            <Anchor key={index} component={Link} to={item.href}>
              {item.title}
            </Anchor>
          ) : (
            <span key={index} style={{ color: 'var(--mantine-color-dimmed)' }}>
              {item.title}
            </span>
          )
        ))}
      </Breadcrumbs>

      <Group justify="space-between" mb="xl">
        <Title order={2}>{t('campaigns.edit_campaign') || 'Edit Campaign'}</Title>
        <Button
          variant="subtle"
          leftSection={<IconArrowLeft size={16} />}
          onClick={() => navigate(`/campaigns/${id}`)}
        >
          {t('common.cancel') || 'Cancel'}
        </Button>
      </Group>

      <EditCampaignForm
        campaign={campaign}
        onSubmit={handleSubmit}
        loading={updateCampaign.isPending}
      />
    </Container>
  )
}